import { translateEnum } from 'aos-helpers/src/helpers/translations/Translations'
import { Color } from 'aos-ui-common/src/styles/Color'
import { SeverityTone } from 'aos-ui-universal'
import { EnumValues } from 'enum-values'
import { TFunction } from 'i18next'
import { identity, sortBy } from 'lodash'

export enum AosSeverity {
    Maintenance = 'MAINTENANCE',
    Info = 'INFO',
    Notice = 'NOTICE',
    Incident = 'INCIDENT',
    Emergency = 'EMERGENCY',
}

export const isAosSeverity = (value: string): value is AosSeverity =>
    EnumValues.getValues<string>(AosSeverity).includes(value)

export const parseAosSeverity = (value: string): AosSeverity | undefined =>
    isAosSeverity(value) ? value : undefined

export const eventSeverities = [
    AosSeverity.Maintenance,
    AosSeverity.Info,
    AosSeverity.Notice,
    AosSeverity.Incident,
    AosSeverity.Emergency,
]

export const atcEventSeverities = [AosSeverity.Info, AosSeverity.Notice, AosSeverity.Emergency]

export const emergencyTaskSeverities = [AosSeverity.Notice, AosSeverity.Emergency]

export const taskSeverities = [AosSeverity.Info, AosSeverity.Maintenance, AosSeverity.Emergency]

export const recurringTaskSeverities = [AosSeverity.Info, AosSeverity.Maintenance]

export const allSeverities = EnumValues.getValues<AosSeverity>(AosSeverity)

const aosSeverityOrder: Map<AosSeverity, number> = new Map(
    allSeverities.map((t, index) => [t, index] as [AosSeverity, number]),
)

export const severitiesOrderGetter =
    <T>(getter: f.Func1<T, AosSeverity>) =>
    (t: T) =>
        (aosSeverityOrder.get(getter(t)) || 0) * -1

export function sortBySeverity<T>(items: T[], getter: f.Func1<T, AosSeverity>): T[] {
    return sortBy(items, severitiesOrderGetter(getter))
}

export function sortSeverities(items: AosSeverity[]): AosSeverity[] {
    return sortBySeverity(items, identity)
}

// `Color` twin of `severityToneMap`, for surfaces that take a raw colour value
// instead of a tone (map markers, legacy chrome). AosSeverity.spec.ts pins the
// two together so a tone change can't leave this behind.
export const severityColorsMapping: Record<AosSeverity, Color> = {
    [AosSeverity.Maintenance]: Color.StatusNeutral,
    [AosSeverity.Info]: Color.StatusSuccess,
    [AosSeverity.Emergency]: Color.StatusCritical,
    [AosSeverity.Incident]: Color.StatusCaution,
    [AosSeverity.Notice]: Color.StatusWarning,
}

// Ink that sits on a `severityColorsMapping` fill (Figma node 10615:140798):
// dark on every severity, light only on the deep critical red. The status ramp is
// bright enough that white ink drops under 3:1 on four of the five fills.
export const severityInkColorsMapping: Record<AosSeverity, Color> = {
    [AosSeverity.Maintenance]: Color.ContentOnGraphics,
    [AosSeverity.Info]: Color.ContentOnGraphics,
    [AosSeverity.Emergency]: Color.White,
    [AosSeverity.Incident]: Color.ContentOnGraphics,
    [AosSeverity.Notice]: Color.ContentOnGraphics,
}

export const isSeverityInkDark = (severity: AosSeverity): boolean =>
    severityInkColorsMapping[severity] === Color.ContentOnGraphics

export const severityToneMap: Record<AosSeverity, SeverityTone> = {
    [AosSeverity.Maintenance]: 'maintenance',
    [AosSeverity.Info]: 'normal',
    [AosSeverity.Notice]: 'moderate',
    [AosSeverity.Incident]: 'major',
    [AosSeverity.Emergency]: 'crisis',
}

export const getBackgroundColorFromSeverity = (severity: AosSeverity | undefined) =>
    severity ? severityColorsMapping[severity] : Color.StatusCritical

export const isSeverityHigh = (severity: AosSeverity): boolean =>
    severity === AosSeverity.Emergency || severity === AosSeverity.Incident

export const atcEventSeverityTitle = (t: TFunction) => (e: AosSeverity) =>
    translateEnum<AosSeverity>(t)(AosSeverity, 'atc-event-severity')(e)
export const eventSeverityTitle = (t: TFunction) => (e: AosSeverity) =>
    translateEnum<AosSeverity>(t)(AosSeverity, 'event-severity-new')(e)
export const translateTaskSeverity = (t: TFunction) => (e: AosSeverity) =>
    translateEnum<AosSeverity>(t)(AosSeverity, 'task-severity')(e)
